/**
 * The pure operations the canvas performs on a form document.
 *
 * SEPARATED FROM THE COMPONENT so they can be reasoned about and tested without a DOM. Every one
 * returns a NEW document rather than mutating: React needs the new reference to re-render, and an
 * in-place edit is how a designer ends up showing state the save never sent.
 *
 * ORDER IS RENUMBERED, NEVER PATCHED. Inserting between two items by averaging their orders works
 * until it does not, and the failure is a drag that silently lands in the wrong place. Renumbering
 * the whole section by tens after every move is cheap and cannot drift.
 */
import {DisplayItem, FormDefinition, Placement, Section, Selection, ShapeField, Tab} from "./types";

const STEP = 10;

export function emptyDefinition(shape?: string | null): FormDefinition {
    return {recordTypeRevisionUSID: shape ?? null, sections: [], fields: [], display: []};
}

/** Everything in one section, fields and display widgets together, in the order drawn. */
export type CanvasItem =
    | {kind: "field"; order: number; placement: Placement}
    | {kind: "display"; order: number; index: number; item: DisplayItem};

export function itemsIn(definition: FormDefinition, sectionKey: string | null): CanvasItem[] {
    const items: CanvasItem[] = [];

    (definition.fields ?? []).forEach(placement => {
        if ((placement.section ?? null) === sectionKey) {
            items.push({kind: "field", order: placement.order ?? 0, placement});
        }
    });
    (definition.display ?? []).forEach((item, index) => {
        if ((item.section ?? null) === sectionKey) {
            items.push({kind: "display", order: item.order ?? 0, index, item});
        }
    });

    return items.sort((a, b) => a.order - b.order);
}

/** Codes the shape declares that are not on the canvas yet — what the palette still offers. */
export function unplaced(definition: FormDefinition, shapeFields: ShapeField[]): ShapeField[] {
    const placed = new Set((definition.fields ?? []).map(f => f.code));
    return shapeFields.filter(f => !placed.has(f.Code));
}

export function addSection(definition: FormDefinition, key: string, title: string): FormDefinition {
    const sections = [...(definition.sections ?? []), {key, title, description: null}];
    return {...definition, sections};
}

export function renameSection(definition: FormDefinition, key: string, patch: Partial<Section>): FormDefinition {
    const sections = (definition.sections ?? []).map(s => (s.key === key ? {...s, ...patch} : s));
    return {...definition, sections};
}

/**
 * Remove a section AND everything in it. The alternative — orphaning its contents to "no section" —
 * looks tidier and is worse: the fields reappear in an unlabelled block at the bottom of the form
 * and the person who deleted the section has to work out where they came from.
 */
export function removeSection(definition: FormDefinition, key: string): FormDefinition {
    return {
        ...definition,
        sections: (definition.sections ?? []).filter(s => s.key !== key),
        fields: (definition.fields ?? []).filter(f => (f.section ?? null) !== key),
        display: (definition.display ?? []).filter(d => (d.section ?? null) !== key),
    };
}

export function placeField(definition: FormDefinition, field: ShapeField, sectionKey: string | null,
                           shapeFields: ShapeField[] = []): FormDefinition {
    if ((definition.fields ?? []).some(f => f.code === field.Code)) return definition;

    const placement: Placement = {
        code: field.Code,
        name: field.Name,
        dataTypeRevisionUSID: field.DataTypeRevisionUSID ?? null,
        description: field.Description ?? null,
        retired: !!field.Retired,
        requiredWhen: field.RequiredWhen ?? null,
        section: sectionKey,
        order: nextOrder(definition, sectionKey),
        width: "full",
        // A COMPANION IS SHOWN WHEN ITS FIELD SAYS "OTHER", and the rule is written here rather than
        // asked for. The catalogue already knows both halves -- which field this captures Other for,
        // and which value means Other -- so making the author retype the condition would only give
        // them a way to get it subtly wrong, and getting it wrong is what produces a field that is
        // required while hidden.
        ...(otherShowWhen(field, shapeFields) ?? {}),
    };
    return {...definition, fields: [...(definition.fields ?? []), placement]};
}

/** The show-when a generated "Other" companion should carry, or nothing if it is not one. */
function otherShowWhen(field: ShapeField, shapeFields: ShapeField[]) {
    if (!field.OtherForFieldUSID) return null;

    const parent = shapeFields.find(f => f.USID === field.OtherForFieldUSID);
    if (!parent?.OtherValueUSID) return null;

    return {showWhen: [{field: parent.Code, operator: "IN" as const,
                        value: [parent.OtherValueUSID]}]};
}

/** A field the shape does not declare. It carries its own type because nothing else can supply one. */
export function inventField(definition: FormDefinition, code: string, name: string,
                            dataTypeRevisionUSID: string, sectionKey: string | null): FormDefinition {
    if ((definition.fields ?? []).some(f => f.code === code)) return definition;

    const placement: Placement = {
        code, name, dataTypeRevisionUSID,
        section: sectionKey,
        order: nextOrder(definition, sectionKey),
        width: "full",
        new: true,
    };
    return {...definition, fields: [...(definition.fields ?? []), placement]};
}

export function addDisplay(definition: FormDefinition, kind: DisplayItem["kind"],
                           sectionKey: string | null): FormDefinition {
    const item: DisplayItem = {
        kind, section: sectionKey, order: nextOrder(definition, sectionKey),
        text: kind === "divider" ? null : "",
    };
    return {...definition, display: [...(definition.display ?? []), item]};
}

export function updatePlacement(definition: FormDefinition, code: string, patch: Partial<Placement>): FormDefinition {
    const fields = (definition.fields ?? []).map(f => (f.code === code ? {...f, ...patch} : f));
    return {...definition, fields};
}

export function updateDisplay(definition: FormDefinition, index: number, patch: Partial<DisplayItem>): FormDefinition {
    const display = (definition.display ?? []).map((d, i) => (i === index ? {...d, ...patch} : d));
    return {...definition, display};
}

export function removeField(definition: FormDefinition, code: string): FormDefinition {
    return {...definition, fields: (definition.fields ?? []).filter(f => f.code !== code)};
}

export function removeDisplay(definition: FormDefinition, index: number): FormDefinition {
    return {...definition, display: (definition.display ?? []).filter((_, i) => i !== index)};
}

/**
 * Move an item into a section at a position, and renumber that section.
 *
 * `before` is the item to land in front of, or null for the end. Expressed as "before what" rather
 * than as an index because the canvas knows what the pointer is over, and converting that to an
 * index at the call site is where off-by-ones live.
 */
export function moveInto(definition: FormDefinition, moving: CanvasItem, sectionKey: string | null,
                         before: CanvasItem | null): FormDefinition {
    const current = itemsIn(definition, sectionKey).filter(i => !same(i, moving));
    const at = before ? current.findIndex(i => same(i, before)) : -1;
    const ordered = at >= 0
        ? [...current.slice(0, at), moving, ...current.slice(at)]
        : [...current, moving];

    let next = definition;
    ordered.forEach((item, index) => {
        const order = (index + 1) * STEP;
        next = item.kind === "field"
            ? updatePlacement(next, item.placement.code, {section: sectionKey, order})
            : updateDisplay(next, item.index, {section: sectionKey, order});
    });
    return next;
}

function same(a: CanvasItem, b: CanvasItem): boolean {
    if (a.kind !== b.kind) return false;
    return a.kind === "field" && b.kind === "field"
        ? a.placement.code === b.placement.code
        : (a as any).index === (b as any).index;
}

function nextOrder(definition: FormDefinition, sectionKey: string | null): number {
    const items = itemsIn(definition, sectionKey);
    return items.length ? items[items.length - 1].order + STEP : STEP;
}

/** A key a person can read, derived from the title, kept unique against what already exists. */
export function sectionKeyFor(title: string, existing: Section[]): string {
    const base = (title || "section").toLowerCase().replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "section";
    const taken = new Set(existing.map(s => s.key));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}-${n}`)) n += 1;
    return `${base}-${n}`;
}

/**
 * Which section a click on the palette should add to.
 *
 * THE SELECTED ONE, because that is the only thing on screen that says where the user is working.
 * It used to be the FIRST section always, so on a form with two sections every click landed in
 * Section 1 and the only way to reach Section 2 was to drag -- which is discoverable only if you
 * happen to try it.
 *
 * Selecting a FIELD counts as selecting its section: somebody who just clicked a question in
 * Section 2 and then clicks a palette item means "next to that one", not "back at the top".
 *
 * Falls back to the last section rather than the first, because a form is built top-down and the
 * section somebody just added is the one they are filling.
 */
export function targetSectionFor(definition: FormDefinition, selection: Selection): string | null {
    const sections = definition.sections ?? [];
    if (!sections.length) return null;

    if (selection?.kind === "section") return selection.key;

    if (selection?.kind === "field") {
        const placement = (definition.fields ?? []).find(f => f.code === selection.code);
        if (placement?.section) return placement.section;
    }

    if (selection?.kind === "display") {
        const item = (definition.display ?? [])[selection.index];
        if (item?.section) return item.section;
    }

    return sections[sections.length - 1].key;
}

/**
 * Whether this placement can be REQUIRED while HIDDEN.
 *
 * The server behaves correctly when it happens -- the save refuses and names the field -- but it
 * names a field nobody can see, and the person who can fix it is the one authoring the form, now.
 *
 * SOUND, NOT COMPLETE, and the direction of the error is chosen. It reports safe only when the
 * visibility rule is IMPLIED by the requirement rule: if every visibility condition also appears in
 * the requirement rule, then whenever the field is required all of those conditions hold, so it is
 * also shown. Anything else warns.
 *
 * That over-warns on rules that are equivalent without being identical -- `IN [a]` versus
 * `EQUAL a`, say. Deliberate: the alternative is deciding implication between arbitrary conditions,
 * which is a solver, and a warning somebody can read and dismiss is a much smaller cost than a
 * missed one that surfaces as a refused save naming an invisible field.
 */
export function hiddenButRequired(placement: Placement): boolean {
    const required = placement.requiredWhen;
    const shown = placement.showWhen;

    // Never required, so it can never be required-while-hidden.
    if (required === null || required === undefined) return false;
    // Always visible.
    if (!Array.isArray(shown) || shown.length === 0) return false;
    // Anything that is not a list is not a rule this can reason about; say nothing rather than warn
    // about a shape somebody else will interpret.
    if (!Array.isArray(required)) return false;

    // An EMPTY requirement list means ALWAYS required, and falls through deliberately: there are no
    // conditions for the visibility rule to be a subset of, so a field that is always required and
    // sometimes hidden is reported.
    const requiredKeys = new Set(required.map(conditionKey));
    return !shown.every(condition => requiredKeys.has(conditionKey(condition)));
}

/** Every placed field that could be required while hidden. */
export function hiddenButRequiredCodes(definition: FormDefinition): string[] {
    return (definition.fields ?? []).filter(hiddenButRequired).map(f => f.code);
}

/** One condition, normalised so two spellings of the same thing compare equal. */
function conditionKey(condition: any): string {
    const value = Array.isArray(condition?.value)
        ? [...condition.value].map(String).sort()
        : condition?.value ?? null;
    return JSON.stringify({
        field: condition?.field ?? null,
        operator: String(condition?.operator ?? "EQUAL").toUpperCase(),
        value,
    });
}

/** Tabs group sections; a section says which tab it is under. See types.ts. */
export function addTab(definition: FormDefinition, key: string, title: string): FormDefinition {
    const tabs = definition.tabs ?? [];
    return {...definition, tabs: [...tabs, {key, title, order: (tabs.length + 1) * 10}]};
}

export function renameTab(definition: FormDefinition, key: string, patch: Partial<Tab>): FormDefinition {
    return {...definition, tabs: (definition.tabs ?? []).map(t => (t.key === key ? {...t, ...patch} : t))};
}

/**
 * Remove a tab, leaving its SECTIONS in place and outside the tabs.
 *
 * The opposite choice from removing a section, which takes its fields with it — and the difference is
 * what is lost. A section holds questions; deleting it deletes them from the form. A tab holds only
 * an arrangement, so removing one should cost the arrangement and nothing else. Sections falling out
 * of the tabs are still shown, because a section outside the tabs is always shown.
 */
export function removeTab(definition: FormDefinition, key: string): FormDefinition {
    return {
        ...definition,
        tabs: (definition.tabs ?? []).filter(t => t.key !== key),
        sections: (definition.sections ?? []).map(s => (s.tab === key ? {...s, tab: null} : s)),
    };
}

/** Which sections the canvas should draw for the open tab, following the renderer's rule. */
export function sectionsFor(definition: FormDefinition, openTab: string | null): Section[] {
    const sections = definition.sections ?? [];
    if (!(definition.tabs ?? []).length) return sections;
    // A section outside the tabs is ALWAYS shown — the same rule the renderer follows, so the canvas
    // is not quietly a different form from the one people fill in.
    return sections.filter(s => !s.tab || s.tab === openTab);
}
